import { Controller } from "stimulus";

/**
 * Manages the evaluator configuration UI for UserNotification::Rule forms.
 * Shows/hides evaluator-specific config sections based on the selected
 * evaluator type, and dynamically loads ParcelOverlayType fields.
 *
 * Targets:
 *   evaluatorSelect  - the evaluator_type <select>
 *   overlaySection   - wrapper div for ParcelOverlay config (shown/hidden)
 *   overlaySelect    - the ParcelOverlayType <select>
 *   fieldsContainer  - where dynamic field checkboxes are rendered
 *   configInput      - hidden input holding the evaluator_config JSON
 */
export default class extends Controller {
  static targets = [
    "evaluatorSelect",
    "overlaySection",
    "overlaySelect",
    "fieldsContainer",
    "configInput"
  ];

  connect() {
    this.fieldsUrl = this.data.get("fieldsUrl");
    this.syncVisibility();
    this.restoreFromConfig();
  }

  evaluatorChanged() {
    this.syncVisibility();
    this.buildConfig();
  }

  overlayTypeChanged() {
    var overlayTypeId = this.overlaySelectTarget.value;
    if (!overlayTypeId) {
      this.fieldsContainerTarget.innerHTML = "";
      this.buildConfig();
      return;
    }
    this.loadOverlayFields(overlayTypeId);
  }

  fieldChanged() {
    this.buildConfig();
  }

  syncVisibility() {
    var evaluatorType = this.evaluatorSelectTarget.value;
    var isOverlay = evaluatorType === "UserNotification::Evaluator::ParcelOverlay";

    if (this.hasOverlaySectionTarget) {
      if (isOverlay) {
        this.overlaySectionTarget.classList.remove("hidden");
      } else {
        this.overlaySectionTarget.classList.add("hidden");
      }
    }
  }

  async loadOverlayFields(overlayTypeId) {
    var url = this.fieldsUrl.replace("__ID__", overlayTypeId);

    try {
      var response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        }
      });

      if (response.ok) {
        var data = await response.json();
        this.renderFields(data.fields);
        this.restoreFieldSelections();
        this.buildConfig();
      }
    } catch (error) {
      console.error("Error loading overlay type fields:", error);
    }
  }

  renderFields(fields) {
    this.fieldsContainerTarget.innerHTML = "";

    if (fields.length === 0) {
      var empty = document.createElement("p");
      empty.className = "text-sm text-gray-500 italic";
      empty.textContent = "This overlay type has no configurable fields.";
      this.fieldsContainerTarget.appendChild(empty);
      return;
    }

    fields.forEach(function(field) {
      var wrapper = document.createElement("div");
      wrapper.className = "space-y-1";
      wrapper.dataset.fieldSlug = field.slug;

      var label = document.createElement("label");
      label.className = "block text-sm font-medium text-gray-700";
      label.textContent = field.name;

      wrapper.appendChild(label);

      if (field.choices && field.choices.length > 0) {
        var hint = document.createElement("p");
        hint.className = "text-xs text-gray-500";
        hint.textContent = "Select values that should trigger this notification:";
        wrapper.appendChild(hint);

        var choicesDiv = document.createElement("div");
        choicesDiv.className = "flex flex-wrap gap-2 mt-1";

        field.choices.forEach(function(choice) {
          var choiceLabel = document.createElement("label");
          choiceLabel.className = "inline-flex items-center gap-1 text-sm bg-white border border-gray-200 rounded px-2 py-1 cursor-pointer hover:bg-gray-50";

          var checkbox = document.createElement("input");
          checkbox.type = "checkbox";
          checkbox.className = "rounded border-gray-300 text-purple-600 focus:ring-purple-500";
          checkbox.dataset.action = "change->evaluator-config#fieldChanged";
          checkbox.dataset.fieldSlug = field.slug;
          checkbox.value = choice;

          var text = document.createElement("span");
          text.textContent = choice;

          choiceLabel.appendChild(checkbox);
          choiceLabel.appendChild(text);
          choicesDiv.appendChild(choiceLabel);
        });

        wrapper.appendChild(choicesDiv);
      } else {
        var hint = document.createElement("p");
        hint.className = "text-xs text-gray-500 italic";
        hint.textContent = "Presence of this overlay type will be checked (no value matching for this field).";
        wrapper.appendChild(hint);
      }

      this.fieldsContainerTarget.appendChild(wrapper);
    }.bind(this));
  }

  buildConfig() {
    var evaluatorType = this.evaluatorSelectTarget.value;

    if (evaluatorType !== "UserNotification::Evaluator::ParcelOverlay") {
      this.configInputTarget.value = "{}";
      return;
    }

    var overlayTypeId = this.overlaySelectTarget.value;
    if (!overlayTypeId) {
      this.configInputTarget.value = "{}";
      return;
    }

    var config = { parcel_overlay_type_id: parseInt(overlayTypeId, 10) };
    var datasetMatch = {};

    var checkboxes = this.fieldsContainerTarget.querySelectorAll("input[type='checkbox']:checked");
    checkboxes.forEach(function(cb) {
      var slug = cb.dataset.fieldSlug;
      if (!datasetMatch[slug]) {
        datasetMatch[slug] = [];
      }
      datasetMatch[slug].push(cb.value);
    });

    if (Object.keys(datasetMatch).length > 0) {
      config.dataset_match = datasetMatch;
    }

    this.configInputTarget.value = JSON.stringify(config);
  }

  restoreFromConfig() {
    var configJson = this.configInputTarget.value;
    if (!configJson || configJson === "{}") return;

    try {
      var config = JSON.parse(configJson);
      if (config.parcel_overlay_type_id && this.hasOverlaySelectTarget) {
        this.overlaySelectTarget.value = String(config.parcel_overlay_type_id);
        if (this.overlaySelectTarget.value) {
          this.loadOverlayFields(config.parcel_overlay_type_id);
        }
      }
    } catch (e) {
      // Invalid JSON, ignore
    }
  }

  restoreFieldSelections() {
    var configJson = this.configInputTarget.value;
    if (!configJson || configJson === "{}") return;

    try {
      var config = JSON.parse(configJson);
      var datasetMatch = config.dataset_match || {};

      Object.keys(datasetMatch).forEach(function(slug) {
        var values = Array.isArray(datasetMatch[slug]) ? datasetMatch[slug] : [datasetMatch[slug]];
        values.forEach(function(val) {
          var cb = this.fieldsContainerTarget.querySelector(
            "input[type='checkbox'][data-field-slug='" + slug + "'][value='" + val + "']"
          );
          if (cb) cb.checked = true;
        }.bind(this));
      }.bind(this));
    } catch (e) {
      // Invalid JSON, ignore
    }
  }
}
